import { createContext, ReactNode, useContext } from 'react';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import { useContactFormData } from 'src/SharedCustomHooks/UseContactFormData/useContactFormData';

interface ContactFormProviderProps {
  children: ReactNode;
  initialData?: Partial<IContactFormData>;
}

export interface ContactFormDataContext {
  contactFormData: IContactFormData;
  isLoading: boolean;
  errors: Record<string, string>;
  location: string;
  predictions: Array<{ description: string; [key: string]: any }>;
}

export interface ContactFormActionsContext {
  setContactFormData: (data: Partial<IContactFormData>) => void;
  updateContactFormData: (updates: Partial<IContactFormData>) => void;
  clearContactFormData: () => void;
  setErrors: (newErrors: Record<string, string>) => void;
  clearError: (field: string) => void;
  setLoading: (loading: boolean) => void;
  validateContactFormData: () => {
    isValid: boolean;
    errors: Record<string, string>;
  };
  isSubmitDisabled: () => boolean;
  clearSuites: () => void;
  setLocation: (location: string) => void;
  setPredictions: (
    predictions: Array<{ description: string; [key: string]: any }>
  ) => void;
  clearLocationSearch: () => void;
  getLocation: (value: string) => Promise<void>;
  onClickLocation: (address: string) => Promise<void>;
  addPropertyToSelected: (newSelectedProperties: any[]) => void;
  removePropertyFromSelected: (remainingProperties: any[]) => void;
  addNewSuite: (newSelectedSuites: any[]) => void;
  removeSuite: (newSelectedSuites: any[]) => void;
  initializeContactFormDataFromEdit: (
    editData: Partial<IContactFormData>
  ) => void;
  clearSuitesInFormForRemovedProperty: (selectedPropertyToRemove: any) => void;
}

export function createContactFormContextProvider() {
  const DataContext = createContext<any | undefined>(undefined);
  const ActionsContext = createContext<any | undefined>(undefined);

  const ContactFormProvider = ({ children }: ContactFormProviderProps) => {
    const contactFormHook: any = useContactFormData();

    const dataValue: ContactFormDataContext = {
      contactFormData: contactFormHook.contactFormData,
      isLoading: contactFormHook.isLoading,
      errors: contactFormHook.errors,
      location: contactFormHook.location,
      predictions: contactFormHook.predictions,
    };

    const actionsValue: ContactFormActionsContext = {
      setContactFormData: contactFormHook.setContactFormData,
      updateContactFormData: contactFormHook.updateContactFormData,
      clearContactFormData: contactFormHook.clearContactFormData,
      setErrors: contactFormHook.setErrors,
      clearError: contactFormHook.clearError,
      setLoading: contactFormHook.setLoading,
      validateContactFormData: contactFormHook.validateContactFormData,
      isSubmitDisabled: contactFormHook.isSubmitDisabled,
      clearSuites: contactFormHook.clearSuites,
      // Location search actions
      setLocation: contactFormHook.setLocation,
      setPredictions: contactFormHook.setPredictions,
      clearLocationSearch: contactFormHook.clearLocationSearch,
      getLocation: contactFormHook.getLocation,
      onClickLocation: contactFormHook.onClickLocation,
      addPropertyToSelected: contactFormHook.addPropertyToSelected,
      removePropertyFromSelected: contactFormHook.removePropertyFromSelected,
      addNewSuite: contactFormHook.addNewSuite,
      removeSuite: contactFormHook.removeSuite,
      initializeContactFormDataFromEdit:
        contactFormHook.initializeContactFormDataFromEdit,
      clearSuitesInFormForRemovedProperty:
        contactFormHook.clearSuitesInFormForRemovedProperty,
    };

    return (
      <ActionsContext.Provider value={actionsValue}>
        <DataContext.Provider value={dataValue}>
          {children}
        </DataContext.Provider>
      </ActionsContext.Provider>
    );
  };

  const useContactFormDataFromContext = (): any => {
    const context = useContext(DataContext);
    if (context === undefined) {
      throw new Error(
        'useContactFormDataFromContext must be used within a ContactFormProvider'
      );
    }
    return context;
  };

  const useContactFormActionsFromContext = (): any => {
    const context = useContext(ActionsContext);
    if (context === undefined) {
      throw new Error(
        'useContactFormActionsFromContext must be used within a ContactFormProvider'
      );
    }
    return context;
  };

  return {
    ContactFormProvider,
    useContactFormDataFromContext,
    useContactFormActionsFromContext,
  };
}

export const {
  ContactFormProvider,
  useContactFormDataFromContext,
  useContactFormActionsFromContext,
} = createContactFormContextProvider();
